import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';
import { CarNotFoundError, fetchCarDetail } from './carInventoryService';
import {
  buildVehicleInquiryMessage,
  carTitle,
  formatBodyStyle,
  formatCarPrice,
  formatCarTrim,
  formatOdometer,
  vehicleChatTitle,
  type CarDetail,
} from './carInventoryTypes';
import { CarDealerDisclosure } from './components/CarDealerDisclosure';
import '../store/StorePage.css';
import './CarInventory.css';

export const CarDetailPage: React.FC = () => {
  const { vehicleId = '' } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { startTopicChat } = useChat();
  const [car, setCar] = useState<CarDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'failed' | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const askLock = useRef(false);
  const thumbsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setActiveIndex(0);
    fetchCarDetail(vehicleId, controller.signal)
      .then(setCar)
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof CarNotFoundError) {
          setError('not_found');
        } else {
          console.error('[CarDetailPage] load failed', err);
          setError('failed');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [vehicleId, reloadKey]);

  const photoCount = car?.photos.length ?? 0;

  const goTo = useCallback(
    (index: number) => {
      if (photoCount === 0) return;
      setActiveIndex((index + photoCount) % photoCount);
    },
    [photoCount]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (e.key === 'ArrowLeft') goTo(activeIndex - 1);
      if (e.key === 'ArrowRight') goTo(activeIndex + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, goTo]);

  useEffect(() => {
    const thumb = thumbsRef.current?.children[activeIndex] as HTMLElement | undefined;
    thumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeIndex]);

  const backToListings = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/vehicles');
  };

  const askAboutVehicle = () => {
    if (!car || askLock.current) return;
    askLock.current = true;
    setAsking(true);
    setAskError(null);
    try {
      startTopicChat({
        topicKey: 'vehicle_buy_vehicle',
        title: vehicleChatTitle(car),
        draftMessage: buildVehicleInquiryMessage(car),
        vehicleStock: car.stock_number || undefined,
        vehicleId: car.id || vehicleId,
        forceNewThread: false,
      });
      navigate('/chat');
    } catch (err) {
      console.error('[CarDetailPage] open chat failed', err);
      askLock.current = false;
      setAsking(false);
      setAskError('Unable to open chat. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="store-page car-page">
        <div className="car-detail car-detail--skeleton" aria-busy>
          <div className="car-gallery-main" />
          <div className="car-detail-panel">
            <div className="car-skeleton-line car-skeleton-line--short" />
            <div className="car-skeleton-line" />
            <div className="car-skeleton-line car-skeleton-line--mid" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !car) {
    return (
      <div className="store-page car-page">
        <div className="store-empty" style={{ marginTop: 40 }}>
          <i className="ti ti-car-off" style={{ fontSize: 40, color: '#ccc' }} />
          <p className="store-empty-text">
            {error === 'not_found'
              ? 'This vehicle is no longer available. It may have been sold.'
              : 'Unable to load this vehicle. Please try again.'}
          </p>
          <div className="car-empty-actions">
            {error === 'failed' && (
              <button className="store-empty-btn" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
            )}
            <Link className="store-empty-btn" to="/vehicles">Browse all vehicles</Link>
          </div>
        </div>
      </div>
    );
  }

  const title = carTitle(car);
  const trimLabel = formatCarTrim(car.trim);
  const activePhoto = car.photos[activeIndex];
  const specs: { label: string; value: string; icon: string }[] = [
    { label: 'Year', value: car.year ? String(car.year) : '—', icon: 'ti-calendar' },
    { label: 'Make', value: car.make || '—', icon: 'ti-building-factory-2' },
    { label: 'Model', value: car.model || '—', icon: 'ti-car' },
    { label: 'Trim', value: trimLabel || '—', icon: 'ti-adjustments' },
    { label: 'Body style', value: formatBodyStyle(car.body_style) || '—', icon: 'ti-car-suv' },
    { label: 'Odometer', value: formatOdometer(car.odometer_km), icon: 'ti-gauge' },
    { label: 'Stock #', value: car.stock_number || '—', icon: 'ti-hash' },
    { label: 'VIN', value: car.vin || '—', icon: 'ti-barcode' },
  ];

  return (
    <div className="store-page car-page">
      <nav className="car-breadcrumb car-breadcrumb--detail" aria-label="Breadcrumb">
        <button type="button" className="car-link-btn" onClick={backToListings}>
          <i className="ti ti-arrow-left" aria-hidden /> Back to listings
        </button>
        <span className="car-breadcrumb-trail">
          <Link to="/vehicles">Vehicles</Link>
          <i className="ti ti-chevron-right" aria-hidden />
          <span>{title}</span>
        </span>
      </nav>

      <motion.div
        className="car-detail"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        <section className="car-gallery" aria-label="Vehicle photos">
          <div className="car-gallery-main">
            {activePhoto ? (
              <img key={activePhoto.url} src={activePhoto.url} alt={`${title} — photo ${activeIndex + 1}`} />
            ) : (
              <div className="car-card-img car-card-img--empty">
                <i className="ti ti-car" aria-hidden />
                <span>Photos coming soon</span>
              </div>
            )}
            {photoCount > 1 && (
              <>
                <button
                  type="button"
                  className="car-gallery-nav car-gallery-nav--prev"
                  onClick={() => goTo(activeIndex - 1)}
                  aria-label="Previous photo"
                >
                  <i className="ti ti-chevron-left" />
                </button>
                <button
                  type="button"
                  className="car-gallery-nav car-gallery-nav--next"
                  onClick={() => goTo(activeIndex + 1)}
                  aria-label="Next photo"
                >
                  <i className="ti ti-chevron-right" />
                </button>
                <span className="car-gallery-counter">
                  {activeIndex + 1} / {photoCount}
                </span>
              </>
            )}
          </div>

          {photoCount > 1 && (
            <div className="car-gallery-thumbs" ref={thumbsRef}>
              {car.photos.map((photo, i) => (
                <button
                  key={photo.order}
                  type="button"
                  className={`car-gallery-thumb${i === activeIndex ? ' car-gallery-thumb--active' : ''}`}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Show photo ${i + 1}`}
                  aria-current={i === activeIndex}
                >
                  <img src={photo.url} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </section>

        <aside className="car-detail-panel">
          {car.is_sold && <span className="car-sold-badge car-sold-badge--panel">Sold</span>}
          {!car.is_sold && car.is_special_price && (
            <span className="car-special-badge car-special-badge--panel">Special price</span>
          )}
          {car.body_style && <span className="car-card-eyebrow">{formatBodyStyle(car.body_style)}</span>}
          <h1 className="car-detail-title">{title}</h1>
          {trimLabel && <p className="car-detail-trim">{trimLabel}</p>}
          <CarDealerDisclosure showLocationPin />
          <div className={`car-detail-price${car.is_sold ? ' car-detail-price--sold' : ''}`}>
            {car.is_sold ? (
              <>
                <span className="car-card-price-was">{formatCarPrice(car.price)}</span>
                <span>Sold</span>
              </>
            ) : car.is_special_price && car.list_price && car.list_price > (car.price ?? 0) ? (
              <>
                <span className="car-card-price-was">{formatCarPrice(car.list_price)}</span>
                {formatCarPrice(car.price)}
              </>
            ) : (
              formatCarPrice(car.price)
            )}
          </div>
          <div className="car-detail-highlights">
            <span><i className="ti ti-gauge" aria-hidden /> {formatOdometer(car.odometer_km)}</span>
            {car.year && <span><i className="ti ti-calendar" aria-hidden /> {car.year}</span>}
            <span><i className="ti ti-photo" aria-hidden /> {photoCount} photos</span>
          </div>
          {car.is_sold && (
            <p className="car-sold-note">
              This vehicle has sold. You can still browse the photos and details.
            </p>
          )}

          <h2 className="car-detail-subtitle">Vehicle details</h2>
          <dl className="car-spec-grid">
            {specs.map((spec) => (
              <div key={spec.label} className="car-spec">
                <dt><i className={`ti ${spec.icon}`} aria-hidden /> {spec.label}</dt>
                <dd>{spec.value}</dd>
              </div>
            ))}
          </dl>
          <p className="car-detail-note">
            {car.is_sold
              ? 'Last listed price is shown for reference. Taxes, licensing and fees were not included.'
              : 'Price and availability can change. Taxes, licensing and fees are not included.'}
          </p>
          {!car.is_sold && (
            <>
              {askError && <p className="car-ask-error">{askError}</p>}
              <button
                type="button"
                className="car-ask-btn"
                onClick={askAboutVehicle}
                disabled={asking}
              >
                {asking ? 'Opening chat…' : 'Ask about this vehicle'}
              </button>
            </>
          )}
        </aside>
      </motion.div>
    </div>
  );
};
